import { memo, useRef } from 'react';
import { Vector3 } from 'three';

import {
  CameraControls as BaseCameraControls,
  CameraControlsProps as BaseCameraControlsProps,
  useDollhouse,
  useInteractiveObjects,
} from '@spazious/dollhouse-engine';
import { useStore } from '@spazious/dollhouse-store';
import { CameraControlsImpl } from '@spazious/dollhouse-three';
import { ControlsModeEnum } from '@spazious/dollhouse-types';
import { useEventCallback } from '@spazious/react-hooks';

import { CameraTravelControls } from '../CameraTravelControls';

export interface CameraControlsProps extends BaseCameraControlsProps {}

export const CameraControls: React.FC<CameraControlsProps> = memo(({ ...restProps }) => {
  const controlsRef = useRef<CameraControlsImpl>(null!);
  const set = useStore(({ set }) => set);
  const controls = useStore(({ controls }) => controls);
  const camera = useStore(({ camera }) => camera);
  const world = useDollhouse(({ world }) => world);
  const raycaster = useDollhouse(({ raycaster }) => raycaster);
  const [interactiveObjects] = useInteractiveObjects();

  const walkTest = useEventCallback(() => {
    // TODO: we must only check the first intersection but atm is not possible due to threejs has not raycastfirst - https://discourse.threejs.org/t/stopping-a-raycast-on-first-object-hit/8020
    // btw BVH has this implementation (raycastfirst) - https://github.com/gkjohnson/three-mesh-bvh
    const intersects = raycaster.intersectObjects(interactiveObjects);
    return intersects.length === 0;
  });

  const handleEnd = useEventCallback(() => {
    if (controlsRef.current && controls) {
      const pos = new Vector3();
      controlsRef.current.getPosition(pos);
      set((state) => ({
        ...state,
        camera: {
          ...state.camera,
          position: pos.toArray(),
          azimuthAngle: controlsRef.current.azimuthAngle,
          polarAngle: controlsRef.current.polarAngle,
          fov: (camera as any).fov ?? state.camera.fov,
        },
      }));
    }
  });

  if (controls === ControlsModeEnum.Travelling) {
    if (world?.navMesh) {
      return <CameraTravelControls makeDefault />;
    }
  }

  return (
    <BaseCameraControls
      ref={controlsRef}
      enablePan={camera.enablePan ?? true}
      view={controls as 'orbit' | 'zenithal' | 'walk'}
      world={world}
      {...restProps}
      makeDefault
      walkTest={walkTest}
      onEnd={handleEnd}
    />
  );
});
